import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../config/api';

// Categorical slots 1+2 of the validated palette (worst adjacent CVD dE 73.6,
// aqua sits below 3:1 on white so the legend text + table view carry relief).
const BLUE = '#2a78d6';
const AQUA = '#1baf7a';
const SERIOUS = '#e34948';
const TEXT_PRIMARY = '#0b0b0b';
const TEXT_SECONDARY = '#52514e';
const GRID = '#ececea';
const SURFACE = '#ffffff';

const RANGES = [
  { label: '1h', hours: 1 },
  { label: '6h', hours: 6 },
  { label: '24h', hours: 24 },
  { label: '7d', hours: 24 * 7 },
  { label: '30d', hours: 24 * 30 },
];

const REFRESH_MS = 5000;

const num = (value) => {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const timestampMs = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

function normalizeBucket(row) {
  const bucketMs = timestampMs(row?.bucket_ms);
  if (bucketMs === null) return null;
  return { ...row, bucket_ms: bucketMs };
}

function formatTick(ms, hours) {
  const parsed = timestampMs(ms);
  if (parsed === null) return '—';
  const d = new Date(parsed);
  if (Number.isNaN(d.getTime())) return '—';
  if (hours > 48) {
    return `${d.getMonth() + 1}/${d.getDate()}`;
  }
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatTooltipTime(ms) {
  const parsed = timestampMs(ms);
  if (parsed === null) return '—';
  const d = new Date(parsed);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatAgo(ms) {
  const seconds = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (seconds < 90) return `${seconds}s ago`;
  if (seconds < 5400) return `${Math.round(seconds / 60)}m ago`;
  return `${Math.round(seconds / 3600)}h ago`;
}

/**
 * Small-multiple line chart: 2px lines, recessive horizontal grid, optional
 * max-envelope band for a series, crosshair + single tooltip listing every
 * series at the hovered bucket.
 */
function HealthChart({ title, unit, buckets, series, hours, fixedMax = null }) {
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(null);

  const width = 460;
  const height = 170;
  const pad = { top: 10, right: 12, bottom: 22, left: 40 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const { xMin, xMax, yMax } = useMemo(() => {
    const xs = buckets.map((b) => b.bucket_ms);
    let maxVal = 0;
    buckets.forEach((b) => {
      series.forEach((s) => {
        const v = num(b[s.envelopeKey]) ?? num(b[s.key]);
        if (v !== null && v > maxVal) maxVal = v;
      });
    });
    const computedMax = fixedMax !== null
      ? Math.max(fixedMax, Math.ceil(maxVal * 1.05))
      : Math.max(10, Math.ceil(maxVal * 1.15));
    return {
      xMin: xs.length ? xs[0] : 0,
      xMax: xs.length ? xs[xs.length - 1] : 1,
      yMax: computedMax,
    };
  }, [buckets, series, fixedMax]);

  const toX = useCallback(
    (ms) => pad.left + (xMax === xMin ? plotW / 2 : ((ms - xMin) / (xMax - xMin)) * plotW),
    [xMin, xMax, pad.left, plotW]
  );
  const toY = useCallback(
    (v) => pad.top + plotH - (Math.min(v, yMax) / yMax) * plotH,
    [yMax, pad.top, plotH]
  );

  const linePath = useCallback((key) => {
    let path = '';
    buckets.forEach((b) => {
      const v = num(b[key]);
      if (v === null) return;
      path += `${path ? 'L' : 'M'}${toX(b.bucket_ms).toFixed(1)},${toY(v).toFixed(1)}`;
    });
    return path;
  }, [buckets, toX, toY]);

  const envelopePath = useCallback((avgKey, maxKey) => {
    const points = buckets
      .map((b) => ({ ms: b.bucket_ms, avg: num(b[avgKey]), max: num(b[maxKey]) }))
      .filter((p) => p.avg !== null && p.max !== null);
    if (points.length < 2) return '';
    const top = points.map((p, i) => `${i ? 'L' : 'M'}${toX(p.ms).toFixed(1)},${toY(p.max).toFixed(1)}`).join('');
    const bottom = [...points].reverse().map((p) => `L${toX(p.ms).toFixed(1)},${toY(p.avg).toFixed(1)}`).join('');
    return `${top}${bottom}Z`;
  }, [buckets, toX, toY]);

  const handleMove = (e) => {
    if (!buckets.length) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * width;
    let nearest = 0;
    let best = Infinity;
    buckets.forEach((b, i) => {
      const d = Math.abs(toX(b.bucket_ms) - px);
      if (d < best) { best = d; nearest = i; }
    });
    setHover(nearest);
  };

  const yTicks = [0.25, 0.5, 0.75, 1].map((f) => Math.round(yMax * f));
  const xTickCount = 4;
  const xTicks = buckets.length > 1
    ? Array.from({ length: xTickCount }, (_, i) => xMin + ((xMax - xMin) * i) / (xTickCount - 1))
    : [];

  const hovered = hover !== null ? buckets[hover] : null;
  const tooltipLeft = hovered
    ? Math.min(Math.max(((toX(hovered.bucket_ms) / width) * 100), 12), 78)
    : 0;

  return (
    <div className="sa-card" style={{ padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_PRIMARY }}>{title}</div>
        <div style={{ display: 'flex', gap: 12 }}>
          {series.map((s) => (
            <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: TEXT_SECONDARY }}>
              <span style={{ width: 10, height: 3, borderRadius: 2, background: s.color, display: 'inline-block' }} />
              {s.name}
            </span>
          ))}
          {series.some((s) => s.envelopeKey) && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: TEXT_SECONDARY }}>
              <span style={{ width: 10, height: 8, borderRadius: 2, background: 'rgba(42,120,214,0.12)', display: 'inline-block' }} />
              max range
            </span>
          )}
        </div>
      </div>

      <div ref={wrapRef} style={{ position: 'relative' }}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          style={{ width: '100%', display: 'block', cursor: buckets.length ? 'crosshair' : 'default' }}
          onPointerMove={handleMove}
          onPointerLeave={() => setHover(null)}
        >
          {yTicks.map((t) => (
            <g key={t}>
              <line x1={pad.left} x2={width - pad.right} y1={toY(t)} y2={toY(t)} stroke={GRID} strokeWidth="1" />
              <text x={pad.left - 6} y={toY(t) + 3} textAnchor="end" fontSize="10" fill={TEXT_SECONDARY}>
                {t}{unit}
              </text>
            </g>
          ))}
          <line x1={pad.left} x2={width - pad.right} y1={toY(0)} y2={toY(0)} stroke="#d5d5d2" strokeWidth="1" />
          {xTicks.map((ms, i) => (
            <text key={i} x={toX(ms)} y={height - 6} textAnchor="middle" fontSize="10" fill={TEXT_SECONDARY}>
              {formatTick(ms, hours)}
            </text>
          ))}

          {series.map((s) => s.envelopeKey && (
            <path key={`${s.key}-band`} d={envelopePath(s.key, s.envelopeKey)} fill="rgba(42,120,214,0.12)" stroke="none" />
          ))}
          {series.map((s) => (
            <path
              key={s.key}
              d={linePath(s.key)}
              fill="none"
              stroke={s.color}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}

          {hovered && (
            <g>
              <line
                x1={toX(hovered.bucket_ms)} x2={toX(hovered.bucket_ms)}
                y1={pad.top} y2={pad.top + plotH}
                stroke="#b9b9b4" strokeWidth="1"
              />
              {series.map((s) => {
                const v = num(hovered[s.key]);
                if (v === null) return null;
                return (
                  <circle
                    key={s.key}
                    cx={toX(hovered.bucket_ms)} cy={toY(v)} r="4.5"
                    fill={s.color} stroke={SURFACE} strokeWidth="2"
                  />
                );
              })}
            </g>
          )}
        </svg>

        {hovered && (
          <div style={{
            position: 'absolute', top: 4, left: `${tooltipLeft}%`,
            background: '#fff', border: '1px solid #ddd', borderRadius: 8,
            padding: '6px 10px', fontSize: 11, pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)', whiteSpace: 'nowrap', zIndex: 5,
          }}>
            <div style={{ color: TEXT_SECONDARY, marginBottom: 3 }}>{formatTooltipTime(hovered.bucket_ms)}</div>
            {series.map((s) => {
              const v = num(hovered[s.key]);
              const vMax = s.envelopeKey ? num(hovered[s.envelopeKey]) : null;
              return (
                <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, display: 'inline-block' }} />
                  <strong style={{ color: TEXT_PRIMARY }}>{v === null ? '—' : `${v}${unit}`}</strong>
                  <span style={{ color: TEXT_SECONDARY }}>
                    {s.name}{vMax !== null ? ` (max ${vMax}${unit})` : ''}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, value, unit, warnAt = null }) {
  const isHigh = warnAt !== null && value !== null && value >= warnAt;
  return (
    <div className="sa-card" style={{ padding: '10px 14px', minWidth: 108, flex: 1 }}>
      <div style={{ fontSize: 11, color: TEXT_SECONDARY, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: isHigh ? SERIOUS : TEXT_PRIMARY }}>
        {value === null ? '—' : `${value}${unit}`}
        {isHigh && <span style={{ fontSize: 11, fontWeight: 600, marginLeft: 6 }}>▲ high</span>}
      </div>
    </div>
  );
}

export default function ServerHealth({ token, navigate }) {
  const [hosts, setHosts] = useState([]);
  const [selectedHost, setSelectedHost] = useState(null);
  const [hours, setHours] = useState(24);
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTable, setShowTable] = useState(false);

  const loadHosts = useCallback(async () => {
    const res = await apiFetch('/server-health/hosts', {}, token);
    const data = res.data || [];
    setHosts(data);
    setSelectedHost((prev) => prev || data[0]?.host || null);
    return data;
  }, [token]);

  const loadSeries = useCallback(async (host, rangeHours) => {
    if (!host) return;
    const res = await apiFetch(`/server-health/series?host=${encodeURIComponent(host)}&hours=${rangeHours}&points=200`, {}, token);
    setBuckets((res.data || []).map(normalizeBucket).filter(Boolean));
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    loadHosts()
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [loadHosts]);

  useEffect(() => {
    if (!selectedHost) return undefined;
    let cancelled = false;

    const refresh = () => {
      loadSeries(selectedHost, hours).catch((err) => { if (!cancelled) setError(err.message); });
      loadHosts().catch(() => {});
    };

    refresh();
    const interval = setInterval(refresh, REFRESH_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [selectedHost, hours, loadSeries, loadHosts]);

  const latest = hosts.find((h) => h.host === selectedHost) || null;
  const latestDetail = useMemo(() => {
    if (!latest?.detail) return null;
    try {
      return typeof latest.detail === 'string' ? JSON.parse(latest.detail) : latest.detail;
    } catch {
      return null;
    }
  }, [latest]);

  const charts = [
    {
      title: 'Responsiveness (ping)',
      unit: 'ms',
      fixedMax: null,
      series: [{ key: 'ping_ms', name: 'Ping', color: BLUE, envelopeKey: 'ping_ms_max' }],
    },
    {
      title: 'CPU',
      unit: '%',
      fixedMax: 100,
      series: [{ key: 'cpu_pct', name: 'CPU', color: BLUE, envelopeKey: 'cpu_pct_max' }],
    },
    {
      title: 'Memory & Disk',
      unit: '%',
      fixedMax: 100,
      series: [
        { key: 'mem_pct', name: 'Memory', color: BLUE },
        { key: 'disk_pct', name: 'Disk', color: AQUA },
      ],
    },
    {
      title: 'Network (% of link max)',
      unit: '%',
      fixedMax: null,
      series: [
        { key: 'net_in_pct', name: 'In', color: BLUE },
        { key: 'net_out_pct', name: 'Out', color: AQUA },
      ],
    },
    {
      title: 'Connections',
      unit: '',
      fixedMax: null,
      series: [
        { key: 'tcp_connections', name: 'TCP established', color: BLUE },
        { key: 'socket_clients', name: 'Socket.IO clients', color: AQUA },
      ],
    },
  ];

  return (
    <div>
      <div className="sa-breadcrumb">
        <button onClick={() => navigate('dashboard')}>Schools</button>
        {' / Server Health'}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0 }}>Server Health</h2>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {hosts.map((h) => (
            <button
              key={h.host}
              className="sa-btn"
              style={{
                background: h.host === selectedHost ? '#1c3d5a' : '#fff',
                color: h.host === selectedHost ? '#fff' : '#333',
                border: '1px solid #d5d5d2',
              }}
              onClick={() => setSelectedHost(h.host)}
            >
              {h.host}
            </button>
          ))}
          {RANGES.map((r) => (
            <button
              key={r.label}
              className="sa-btn"
              style={{
                background: r.hours === hours ? '#449CE3' : '#fff',
                color: r.hours === hours ? '#fff' : '#333',
                border: '1px solid #d5d5d2',
              }}
              onClick={() => setHours(r.hours)}
            >
              {r.label}
            </button>
          ))}
          <button
            className="sa-btn"
            style={{ background: showTable ? '#1c3d5a' : '#fff', color: showTable ? '#fff' : '#333', border: '1px solid #d5d5d2' }}
            onClick={() => setShowTable((v) => !v)}
          >
            {showTable ? 'Charts' : 'Table'}
          </button>
        </div>
      </div>

      {error && <div className="sa-error">{error}</div>}
      {loading && <div className="sa-empty">Loading…</div>}

      {!loading && hosts.length === 0 && (
        <div className="sa-empty">
          No health data yet. Start the health agent on a server
          (<code>scripts/health-agent.js</code>) pointed at this environment's
          <code> /api/server-health/ingest</code>.
        </div>
      )}

      {!loading && latest && (
        <>
          <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <StatTile label="Ping" value={num(latest.ping_ms)} unit="ms" warnAt={200} />
            <StatTile label="CPU" value={num(latest.cpu_pct)} unit="%" warnAt={90} />
            <StatTile label="Memory" value={num(latest.mem_pct)} unit="%" warnAt={90} />
            <StatTile label="Disk" value={num(latest.disk_pct)} unit="%" warnAt={85} />
            <StatTile label="Net in" value={num(latest.net_in_pct)} unit="%" warnAt={80} />
            <StatTile label="Net out" value={num(latest.net_out_pct)} unit="%" warnAt={80} />
            <StatTile label="TCP conns" value={num(latest.tcp_connections)} unit="" />
            <StatTile label="Socket.IO" value={num(latest.socket_clients)} unit="" />
          </div>
          <div style={{ fontSize: 11, color: TEXT_SECONDARY, marginBottom: 12 }}>
            Last sample {formatAgo(Number(latest.sampled_at_ms))}
            {latestDetail?.load_1m !== undefined && latestDetail?.load_1m !== null
              ? ` · load ${latestDetail.load_1m} (1m) on ${latestDetail.cpus ?? '?'} CPUs`
              : ''}
            {latestDetail?.net_in_mbps !== undefined && latestDetail?.net_in_mbps !== null
              ? ` · ${latestDetail.net_in_mbps} Mbps in / ${latestDetail.net_out_mbps} Mbps out`
              : ''}
          </div>

          {!showTable && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 12 }}>
              {charts.map((chart) => (
                <HealthChart key={chart.title} {...chart} buckets={buckets} hours={hours} />
              ))}
            </div>
          )}

          {showTable && (
            <div className="sa-card" style={{ padding: 0, overflowX: 'auto' }}>
              <table className="sa-table" style={{ width: '100%', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: 8 }}>Time</th>
                    <th style={{ textAlign: 'right', padding: 8 }}>Ping ms</th>
                    <th style={{ textAlign: 'right', padding: 8 }}>CPU %</th>
                    <th style={{ textAlign: 'right', padding: 8 }}>CPU max %</th>
                    <th style={{ textAlign: 'right', padding: 8 }}>Mem %</th>
                    <th style={{ textAlign: 'right', padding: 8 }}>Disk %</th>
                    <th style={{ textAlign: 'right', padding: 8 }}>Net in %</th>
                    <th style={{ textAlign: 'right', padding: 8 }}>Net out %</th>
                    <th style={{ textAlign: 'right', padding: 8 }}>TCP</th>
                    <th style={{ textAlign: 'right', padding: 8 }}>Socket.IO</th>
                  </tr>
                </thead>
                <tbody>
                  {[...buckets].reverse().slice(0, 100).map((b) => (
                    <tr key={b.bucket_ms} style={{ borderTop: `1px solid ${GRID}` }}>
                      <td style={{ padding: 8 }}>{formatTooltipTime(Number(b.bucket_ms))}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{num(b.ping_ms) ?? '—'}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{num(b.cpu_pct) ?? '—'}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{num(b.cpu_pct_max) ?? '—'}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{num(b.mem_pct) ?? '—'}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{num(b.disk_pct) ?? '—'}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{num(b.net_in_pct) ?? '—'}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{num(b.net_out_pct) ?? '—'}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{num(b.tcp_connections) ?? '—'}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{num(b.socket_clients) ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
